"use client";

import React, { useState } from "react";
import Reveal from "@/components/common/Reveal";
import { Layers, ArrowRight, SlidersHorizontal, ShieldCheck, ArrowDownUp, Gauge, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface SlotRow {
  slotMm: string;
  slotInch: string;
  standardWire: string;
  openArea: string;
  applications: Record<Lang, string>;
}
const slotOpenings: SlotRow[] = [{
  slotMm: "0.020 mm (20 µm)",
  slotInch: "0.0008 in",
  standardWire: "12S / 18S",
  openArea: "8% – 14%",
  applications: {
    en: "Fine resin traps, catalyst retention, micro-straining",
    zh: "精细树脂捕集器、催化剂拦截、微孔滤除",
    es: "Trampas de resina fina, retención de catalizador, microfiltrado",
    de: "Feine Harzfallen, Katalysatorrückhaltung, Mikrofiltration",
    fr: "Pièges à résines fines, rétention de catalyseurs, micro-tamisage",
    pt: "Retentores de resina fina, retenção de catalisadores, microfiltragem"
  }
}, {
  slotMm: "0.050 mm (50 µm)",
  slotInch: "0.0020 in",
  standardWire: "18S / 22S",
  openArea: "15% – 24%",
  applications: {
    en: "Water filtration, pharmaceutical separation, edible oil polish",
    zh: "水质精滤、制药分离、食用油抛光精制",
    es: "Filtración de agua, separación farmacéutica, abrillantado de aceites comestibles",
    de: "Wasserfiltration, pharmazeutische Trennung, Speiseöl-Feinreinigung",
    fr: "Filtration d'eau, séparation pharmaceutique, polissage d'huiles alimentaires",
    pt: "Filtração de água, separação farmacêutica, polimento de óleos comestíveis"
  }
}, {
  slotMm: "0.100 mm (100 µm)",
  slotInch: "0.0040 in",
  standardWire: "22S / 28S",
  openArea: "22% – 35%",
  applications: {
    en: "Intake screens, brewing mash tuns, pulp recovery",
    zh: "取水滤网、啤酒糖化槽滤板、纸浆回收",
    es: "Rejillas de toma, cubas de maceración de cerveza, recuperación de pulpa",
    de: "Einlaufsiebe, Läuterbottiche für Brauereien, Zellstoffrückgewinnung",
    fr: "Crépines de prise d'eau, cuves de brassage, récupération de pâte à papier",
    pt: "Telas de captação de água, tinas de mosto cervejeiro, recuperação de celulose"
  }
}, {
  slotMm: "0.250 mm (250 µm)",
  slotInch: "0.0100 in",
  standardWire: "28S / 34S",
  openArea: "32% – 48%",
  applications: {
    en: "Cooling tower strainers, sand retention, mineral slurries",
    zh: "冷却塔除杂过滤、油水防砂过滤、矿浆分离",
    es: "Filtros de torre de enfriamiento, control de arena, lodos minerales",
    de: "Kühlturmsiebe, Sandrückhaltung, Mineralsuspensionen",
    fr: "Filtres de tours de refroidissement, retenue de sable, boues minérales",
    pt: "Filtros para torres de resfriamento, contenção de areia, polpas minerais"
  }
}, {
  slotMm: "0.500 mm (500 µm)",
  slotInch: "0.0200 in",
  standardWire: "34S / 42S",
  openArea: "42% – 60%",
  applications: {
    en: "Dewatering screens, trommels, vibrating screen decks",
    zh: "脱水筛板、滚筒筛筒、振动筛网板",
    es: "Cribas de deshidratación, trommels, cribas vibratorias",
    de: "Entwässerungssiebe, Trommelsiebe, Vibrationssiebdecks",
    fr: "Tamis d'égouttage, trommels, étages de tamis vibrants",
    pt: "Telas de desaguamento, trommels, decks de peneiras vibratórias"
  }
}, {
  slotMm: "1.000 mm (1000 µm)",
  slotInch: "0.0394 in",
  standardWire: "42S / 60S",
  openArea: "52% – 72%",
  applications: {
    en: "Coarse solids separation, grain screening, trash racks",
    zh: "粗颗粒固液分离、粮食筛选、粗格栅拦截",
    es: "Separación de sólidos gruesos, cribado de granos, rejas de desbaste",
    de: "Grobstoffabtrennung, Getreidesiebung, Rechenanlagen",
    fr: "Séparation de solides grossiers, criblage de grains, grilles de dégrillage",
    pt: "Separação de sólidos grosseiros, peneiramento de grãos, grades de retenção"
  }
}];
interface WireProfileRow {
  code: string;
  width: string;
  height: string;
  reliefAngle: string;
  strength: Record<Lang, string>;
  description: Record<Lang, string>;
}
const wireProfiles: WireProfileRow[] = [{
  code: "12S / 18S",
  width: "1.2 – 1.8 mm",
  height: "2.0 – 3.2 mm",
  reliefAngle: "13° – 15°",
  strength: {
    en: "Light / Micro-Slotting",
    zh: "轻型 / 微细缝隙",
    es: "Ligero / Micro-ranurado",
    de: "Leicht / Mikrospaltung",
    fr: "Léger / Micro-fentes",
    pt: "Leve / Microrranhuras"
  },
  description: {
    en: "Ideal for ultra-fine slots where open area must be preserved without restricting fluid paths.",
    zh: "适用于超细微孔缝隙工况，在不阻碍流体通道的前提下最大化有效开孔率。",
    es: "Ideal para ranuras ultrafinas donde se debe maximizar el área abierta sin restringir los pasos del fluido.",
    de: "Ideal für feinste Spalten, bei denen die freie Siebfläche ohne Drosselung des Flusses maximiert werden muss.",
    fr: "Idéal pour les fentes très fines où la surface ouverte doit être préservée sans brider les écoulements.",
    pt: "Ideal para ranhuras ultrafinas onde a área aberta deve ser preservada sem restringir a passagem do fluido."
  }
}, {
  code: "22S / 28S",
  width: "2.2 – 2.8 mm",
  height: "3.5 – 4.5 mm",
  reliefAngle: "10° – 13°",
  strength: {
    en: "Medium / Standard Process",
    zh: "中型 / 标准工艺",
    es: "Medio / Proceso estándar",
    de: "Mittel / Standardprozesse",
    fr: "Moyen / Procédés standard",
    pt: "Médio / Processo padrão"
  },
  description: {
    en: "The versatile standard for liquid-solid separation, vessel internals, and screen tubes.",
    zh: "固液分离、容器内部构件与标准筛管的通用标准规格配置。",
    es: "El estándar versátil para separación líquido-sólido, internos de recipientes y tubos filtrantes.",
    de: "Der vielseitige Standard für Fest-Flüssig-Trennung, Behältereinbauten und Spaltsiebrohre.",
    fr: "Le standard polyvalent pour la séparation solide-liquide, les internes de cuves et les tubes de tamis.",
    pt: "O padrão versátil para separação sólido-líquido, internos de vasos e tubos filtrantes."
  }
}, {
  code: "34S / 42S",
  width: "3.4 – 4.2 mm",
  height: "5.0 – 6.5 mm",
  reliefAngle: "8° – 11°",
  strength: {
    en: "Heavy / High Hydraulic Load",
    zh: "重型 / 高水力负荷",
    es: "Pesado / Alta carga hidráulica",
    de: "Schwer / Hohe hydraulische Last",
    fr: "Lourd / Forte charge hydraulique",
    pt: "Pesado / Alta carga hidráulica"
  },
  description: {
    en: "Designed for high-differential pressure services, abrasive slurries, and mechanical rakes.",
    zh: "专为高压差工况、磨蚀性矿浆流体及机械刮泥/刮刷系统设计。",
    es: "Diseñado para servicios de alta presión diferencial, lodos abrasivos y rasquetas mecánicas.",
    de: "Entwickelt für hohe Differenzdrücke, abrasive Schlämme und mechanische Rake- bzw. Schabersysteme.",
    fr: "Conçu pour les différentiels de pression élevés, les fluides abrasifs et les racleurs mécaniques.",
    pt: "Projetado para serviços de alta pressão diferencial, polpas abrasivas e raspadores mecânicos."
  }
}, {
  code: "60S / 90S",
  width: "6.0 – 9.0 mm",
  height: "8.0 – 12.0 mm",
  reliefAngle: "6° – 9°",
  strength: {
    en: "Severe Duty / Mining",
    zh: "超重型 / 矿山重载",
    es: "Servicio severo / Minería",
    de: "Extrem robust / Bergbau",
    fr: "Usage intensif / Mines",
    pt: "Serviço pesado / Mineração"
  },
  description: {
    en: "Maximum impact and wear resistance for heavy mining, vibrating shakers, and rotary trommels.",
    zh: "针对重型采矿、振动筛分机及旋转滚筒筛提供卓越的抗冲击与耐磨性能。",
    es: "Máxima resistencia al impacto y al desgaste en minería pesada, cribas vibratorias y trommels rotativos.",
    de: "Höchste Schlag- und Verschleißfestigkeit für Bergbau, Vibrationssiebe und Rotationstrommeln.",
    fr: "Résistance maximale aux chocs et à l'abrasion pour les mines, tamis vibrants et trommels rotatifs.",
    pt: "Máxima resistência ao impacto e ao desgaste para mineração pesada, peneiras vibratórias e trommels rotativos."
  }
}];
interface SupportRodRow {
  type: Record<Lang, string>;
  geometry: Record<Lang, string>;
  primaryBenefit: Record<Lang, string>;
  bestFor: Record<Lang, string>;
}
const supportRods: SupportRodRow[] = [{
  type: {
    en: "Round Rods (Q25 / Q35)",
    zh: "圆形支撑条 (Q25 / Q35)",
    es: "Varillas redondas (Q25 / Q35)",
    de: "Rundstäbe (Q25 / Q35)",
    fr: "Barres rondes (Q25 / Q35)",
    pt: "Barras redondas (Q25 / Q35)"
  },
  geometry: {
    en: "Circular cross-section (Ø 2.5 – 5.0 mm)",
    zh: "圆形截面 (Ø 2.5 – 5.0 mm)",
    es: "Sección transversal circular (Ø 2.5 – 5.0 mm)",
    de: "Kreisförmiger Querschnitt (Ø 2,5 – 5,0 mm)",
    fr: "Section circulaire (Ø 2,5 – 5,0 mm)",
    pt: "Seção transversal circular (Ø 2,5 – 5,0 mm)"
  },
  primaryBenefit: {
    en: "Low hydrodynamic drag and balanced radial support for standard screen tubes.",
    zh: "低流体阻力，为标准筛管提供均衡可靠的径向支撑强度。",
    es: "Baja resistencia hidrodinámica y soporte radial uniforme para tubos de criba estándar.",
    de: "Geringer Strömungswiderstand und ausgewogene Radialstützung für Standard-Spaltsiebrohre.",
    fr: "Faible traînée hydrodynamique et soutien radial équilibré pour tubes crépines standards.",
    pt: "Baixo arrasto hidrodinâmico e suporte radial uniforme para tubos de tela convencionais."
  },
  bestFor: {
    en: "Water well casing, cartridge elements, and standard cylindrical screen baskets.",
    zh: "深井管护套、滤芯组件与标准圆筒形滤篮。",
    es: "Revestimientos de pozos, elementos de cartucho y cestas cilíndricas estándar.",
    de: "Brunnenfilterrohre, Filterelemente und zylindrische Standard-Filterkörbe.",
    fr: "Tubages de puits d'eau, éléments de cartouches et paniers filtrants cylindriques.",
    pt: "Revestimentos de poços artesianos, elementos tipo cartucho e cestos cilíndricos padrão."
  }
}, {
  type: {
    en: "Flat Bar / Rectangular",
    zh: "扁钢 / 矩形支撑条",
    es: "Pletina / Barra rectangular",
    de: "Flachstahl / Rechteckprofile",
    fr: "Fer plat / Rectangulaire",
    pt: "Barra chata / Retangular"
  },
  geometry: {
    en: "Narrow vertical strips (10x2 mm up to 50x6 mm)",
    zh: "窄条垂直扁钢 (10×2 mm 至 50×6 mm)",
    es: "Tiras verticales estrechas (10x2 mm hasta 50x6 mm)",
    de: "Schmale vertikale Stege (10x2 mm bis 50x6 mm)",
    fr: "Lamelles verticales étroites (10x2 mm jusqu'à 50x6 mm)",
    pt: "Tiras verticais estreitas (10x2 mm até 50x6 mm)"
  },
  primaryBenefit: {
    en: "Superior beam stiffness along the flow axis for flat panels and vibratory screens.",
    zh: "沿流动轴向具有超高梁抗弯刚度，专为平板与振动筛网设计。",
    es: "Rigidez de viga superior a lo largo del eje de flujo para paneles planos y cribas vibratorias.",
    de: "Hervorragende Biegesteifigkeit in Strömungsrichtung für Flachsiebe und Vibrationssiebe.",
    fr: "Rigidité de poutre supérieure le long de l'axe d'écoulement pour panneaux plats et tamis vibrants.",
    pt: "Excelente rigidez de viga ao longo do eixo de fluxo para painéis planos e telas vibratórias."
  },
  bestFor: {
    en: "Dewatering panels, static sieve bends, and high-impact vibratory decks.",
    zh: "脱水滤板、静态弧形筛板与高冲击振动筛床。",
    es: "Paneles de deshidratación, arcos estáticos de criba y mesas vibratorias de alto impacto.",
    de: "Entwässerungsplatten, statische Bogensiebe und hochbelastbare Vibrationsdecks.",
    fr: "Panneaux d'égouttage, grilles courbes statiques et tamis vibrants à forts impacts.",
    pt: "Painéis de desaguamento, telas curvas estáticas e decks vibratórios de alto impacto."
  }
}, {
  type: {
    en: "Teardrop / Wedge Rods",
    zh: "水滴形 / 楔形支撑条",
    es: "Varillas en gota de agua / Cuña",
    de: "Tropfenförmige Stützstäbe / Keilstäbe",
    fr: "Barres goutte d'eau / Trapézoïdales",
    pt: "Barras gota d'água / Cunha"
  },
  geometry: {
    en: "Tapered profile with aerodynamic trailing edge",
    zh: "流线型渐缩截面，具有低阻力后缘",
    es: "Perfil cónico con borde de fuga hidrodinámico",
    de: "Konisches Profil mit strömungsgünstiger Abrisskante",
    fr: "Profil profilé avec bord de fuite aérodynamique",
    pt: "Perfil afilado com borda de fuga aerodinâmica"
  },
  primaryBenefit: {
    en: "Minimizes backpressure and flow stagnation behind support junctions.",
    zh: "最大程度降低背压，消除支撑焊点背后的流体滞留死区。",
    es: "Minimiza la contrapresión y la recirculación del flujo detrás de las uniones soldadas.",
    de: "Minimiert Gegendruck und Totzonen hinter den Verbindungspunkten der Stäbe.",
    fr: "Minimise la contre-pression et les zones de stagnation derrière les jonctions soudées.",
    pt: "Minimiza a contrapressão e a estagnação de fluxo atrás dos pontos de suporte."
  },
  bestFor: {
    en: "High-velocity radial flow reactors and gas-solid catalyst bed support grids.",
    zh: "高流速径向流动反应器与气固催化剂床层支撑格栅。",
    es: "Reactores de flujo radial de alta velocidad y rejillas de soporte para lechos catalíticos gas-sólido.",
    de: "Radialstrom-Reaktoren mit hohen Geschwindigkeiten und Gas-Feststoff-Katalysatorroste.",
    fr: "Réacteurs à flux radial à grande vitesse et grilles de support pour lits de catalyseurs.",
    pt: "Reatores de fluxo radial de alta velocidade e grades de sustentação de leitos de catalisador gás-sólido."
  }
}];
interface AlloyRow {
  grade: string;
  category: Record<Lang, string>;
  resistance: Record<Lang, string>;
  costIndex: Record<Lang, string>;
  typicalUse: Record<Lang, string>;
}
const alloys: AlloyRow[] = [{
  grade: "AISI 304 / 304L",
  category: {
    en: "Austenitic",
    zh: "奥氏体不锈钢",
    es: "Austenítico",
    de: "Austenitisch",
    fr: "Austénitique",
    pt: "Austenítico"
  },
  resistance: {
    en: "Standard atmospheric, fresh water, and general organic chemicals.",
    zh: "适用于常规大气环境、淡水介质及通用有机化学品。",
    es: "Atmósfera estándar, agua dulce y productos químicos orgánicos generales.",
    de: "Standard-Atmosphäre, Süßwasser und gängige organische Chemikalien.",
    fr: "Atmosphère standard, eau douce et produits chimiques organiques généraux.",
    pt: "Atmosfera convencional, água doce e compostos químicos orgânicos gerais."
  },
  costIndex: {
    en: "Baseline",
    zh: "基准成本",
    es: "Base",
    de: "Basis",
    fr: "Base",
    pt: "Base"
  },
  typicalUse: {
    en: "Food and beverage, industrial water treatment, municipal screening.",
    zh: "食品饮料、工业水处理、市政给排水粗细筛分。",
    es: "Alimentos y bebidas, tratamiento de agua industrial, cribado municipal.",
    de: "Lebensmittel & Getränke, industrielle Wasseraufbereitung, kommunale Siebung.",
    fr: "Alimentaire et boissons, traitement des eaux industrielles, tamisage municipal.",
    pt: "Alimentos e bebidas, tratamento de água industrial, peneiramento municipal."
  }
}, {
  grade: "AISI 316 / 316L",
  category: {
    en: "Moly-Austenitic",
    zh: "含钼奥氏体不锈钢",
    es: "Austenítico con molibdeno",
    de: "Molybdän-Austenitisch",
    fr: "Austénitique au molybdène",
    pt: "Austenítico com molibdênio"
  },
  resistance: {
    en: "High resistance to pitting and crevice corrosion in chloride-bearing media.",
    zh: "在含氯离子介质中具有极高的抗点蚀与缝隙腐蚀性能。",
    es: "Alta resistencia a la corrosión por picaduras y rendijas en medios clorados.",
    de: "Hohe Beständigkeit gegen Loch- und Spaltkorrosion in chloridhaltigen Medien.",
    fr: "Haute résistance à la corrosion par piqûres et caverneuse en milieux chlorés.",
    pt: "Alta resistência à corrosão por pite e em frestas em meios com cloretos."
  },
  costIndex: {
    en: "+25% to 35%",
    zh: "+25% 至 35%",
    es: "+25% a 35%",
    de: "+25% bis 35%",
    fr: "+25% à 35%",
    pt: "+25% a 35%"
  },
  typicalUse: {
    en: "Chemical processing, marine filtration, pharmaceuticals, petrochemicals.",
    zh: "化学工艺过程、海洋与海水过滤、制药工程、石油化工。",
    es: "Procesamiento químico, filtración marina, farmacéutica, petroquímica.",
    de: "Chemische Verfahrenstechnik, Meerwasserfiltration, Pharma, Petrochemie.",
    fr: "Chimie industrielle, filtration marine, pharmacie, pétrochimie.",
    pt: "Processamento químico, filtração naval e marítima, farmacêutica, petroquímica."
  }
}, {
  grade: "Duplex 2205 (UNS S32205)",
  category: {
    en: "Austenitic-Ferritic",
    zh: "双相不锈钢 (2205)",
    es: "Austenítico-ferrítico (Dúplex)",
    de: "Austenitisch-Ferritisch (Duplex)",
    fr: "Austéno-ferritique (Duplex)",
    pt: "Austenítico-ferrítico (Duplex)"
  },
  resistance: {
    en: "High mechanical strength with high stress-corrosion cracking resistance.",
    zh: "具备优异的高机械强度与突出的抗应力腐蚀开裂（SCC）能力。",
    es: "Alta resistencia mecánica y elevada resistencia a la corrosión bajo tensión.",
    de: "Hohe mechanische Festigkeit und hohe Beständigkeit gegen Spannungsrisskorrosion.",
    fr: "Haute résistance mécanique et haute résistance à la corrosion sous contrainte.",
    pt: "Elevada resistência mecânica e excelente resistência à corrosão sob tensão."
  },
  costIndex: {
    en: "+60% to 80%",
    zh: "+60% 至 80%",
    es: "+60% a 80%",
    de: "+60% bis 80%",
    fr: "+60% à 80%",
    pt: "+60% a 80%"
  },
  typicalUse: {
    en: "Oil and gas subsea strainers, pulp digesters, desalination intake units.",
    zh: "油气水下过滤器、造纸纸浆蒸煮器、海水淡化取水装置。",
    es: "Filtros submarinos de petróleo y gas, digestores de pulpa, tomas de desalación.",
    de: "Subsea-Filter für Öl & Gas, Zellstoffkocher, Einläufe von Entsalzungsanlagen.",
    fr: "Crépines sous-marines pétrole et gaz, digesteurs de pâte, prises de dessalement.",
    pt: "Filtros submarinos de óleo e gás, digestores de celulose, tomadas de dessalinização."
  }
}, {
  grade: "Hastelloy C-276",
  category: {
    en: "Nickel Superalloy",
    zh: "镍基超耐蚀合金",
    es: "Superaleación de níquel",
    de: "Nickel-Superlegierung",
    fr: "Superalliage de nickel",
    pt: "Superliga de níquel"
  },
  resistance: {
    en: "Extreme resistance to strong acids, wet chlorine, and sour hydrocarbon streams.",
    zh: "对强酸、湿氯气及高含硫酸性油气介质具有极强耐腐蚀性。",
    es: "Resistencia extrema a ácidos fuertes, cloro húmedo y corrientes de hidrocarburos amargos.",
    de: "Extrem beständig gegen starke Säuren, feuchtes Chlor und saure Kohlenwasserstoffströme.",
    fr: "Résistance extrême aux acides forts, au chlore humide et aux flux d'hydrocarbures acides.",
    pt: "Resistência extrema a ácidos fortes, cloro úmido e correntes ácidas de hidrocarbonetos."
  },
  costIndex: {
    en: "+300%+",
    zh: "+300%+",
    es: "+300%+",
    de: "+300%+",
    fr: "+300%+",
    pt: "+300%+"
  },
  typicalUse: {
    en: "Severe chemical synthesis, flue-gas desulfurization, acidic catalysts.",
    zh: "苛刻化学合成、烟气脱硫脱硝（FGD）、强酸催化床层。",
    es: "Síntesis química severa, desulfuración de gases de combustión, catalizadores ácidos.",
    de: "Aggressive Chemiesynthese, Rauchgasentschwefelung, saure Katalysatorbetten.",
    fr: "Synthèse chimique extrême, désulfuration des gaz de combustion, catalyseurs acides.",
    pt: "Síntese química severa, dessulfurização de gases de combustão, leitos catalíticos ácidos."
  }
}];
interface Copy {
  eyebrow: string;
  heading: string;
  lead: string;
  sec1Badge: string;
  sec1Title: string;
  sec1Subtitle: string;
  formulaLabel: string;
  formulaValue: string;
  colSlotMm: string;
  colSlotInch: string;
  colWire: string;
  colOpenArea: string;
  colApplications: string;
  sec2Title: string;
  sec2Desc: string;
  principleLabel: string;
  principleText: string;
  labelWidth: string;
  labelRelief: string;
  sec3Title: string;
  sec3Desc: string;
  labelAdvantage: string;
  labelCommonApp: string;
  pitchLabel: string;
  pitchText: string;
  sec4Title: string;
  sec4Desc: string;
  tabFoti: string;
  tabFito: string;
  fotiBadge: string;
  fotiCode: string;
  fotiHeading: string;
  fotiText: string;
  fotiAdvTitle: string;
  fotiAdvText: string;
  fotiAppTitle: string;
  fotiAppText: string;
  fotiCardEyebrow: string;
  fotiCardTitle: string;
  fotiCardText: string;
  fotiCardBtn: string;
  fitoBadge: string;
  fitoCode: string;
  fitoHeading: string;
  fitoText: string;
  fitoAdvTitle: string;
  fitoAdvText: string;
  fitoAppTitle: string;
  fitoAppText: string;
  fitoCardEyebrow: string;
  fitoCardTitle: string;
  fitoCardText: string;
  fitoCardBtn: string;
  sec5Title: string;
  sec5Desc: string;
  labelTypicalUses: string;
  crossLinksTitle: string;
  crossLinksDesc: string;
  btnWedgeScreens: string;
  btnScreenTubes: string;
}
const copyData: Record<Lang, Copy> = {
  en: {
    eyebrow: "Technical Specification & Design",
    heading: "Wedge wire screen engineering guide",
    lead: "A comprehensive reference for sizing slot openings, selecting wire profiles, evaluating support structures, and calculating flow orientation across industrial separation systems.",
    sec1Badge: "1. Slot Opening & Open Area Calculation",
    sec1Title: "1. Slot Opening & Open Area Calculation",
    sec1Subtitle: "Continuous slot widths define particle cutoff, hydraulic capacity, and differential pressure.",
    formulaLabel: "Open Area (%) = ",
    formulaValue: "[Slot / (Slot + Wire Width)] × 100",
    colSlotMm: "Slot Opening (mm / µm)",
    colSlotInch: "Imperial (Inches)",
    colWire: "Recommended Wire Profile",
    colOpenArea: "Typical Open Area",
    colApplications: "Typical Industrial Service",
    sec2Title: "2. V-Wire Profile Selection",
    sec2Desc: "Continuous V-shaped wire profiles provide two-point particle contact, preventing clogging and blinding.",
    principleLabel: "Non-Clogging Principle: ",
    principleText: "Particles only touch the outer boundary edges. As particles pass the slot edge, the opening widens inward, letting cleared media discharge cleanly.",
    labelWidth: "Width",
    labelRelief: "Relief",
    sec3Title: "3. Support Rod Structures",
    sec3Desc: "Resistance-welded support rods bear structural tension, collapse pressure, and cyclic shock loads.",
    labelAdvantage: "Advantage: ",
    labelCommonApp: "Common Application: ",
    pitchLabel: "Pitch & Spacing: ",
    pitchText: "Typical rod centers range from 12 mm to 50 mm based on differential pressure (Delta P) requirements and burst rating calculations.",
    sec4Title: "4. Flow Direction & Construction Geometry",
    sec4Desc: "Wedge wire cylinders and screen tubes are configured in either standard or inverted orientation depending on the filtration mechanism.",
    tabFoti: "Outside-to-Inside (FOTI)",
    tabFito: "Inside-to-Outside (FITO)",
    fotiBadge: "Standard Radial Construction",
    fotiCode: "FOTI: Flow Out-To-In",
    fotiHeading: "Smooth External Surface with Internal Support Rods",
    fotiText: "In the standard configuration, the flat faces of the profile wire wrap circumferentially around axial support rods on the interior. Debris is retained on the outer smooth surface, while clean filtrate travels through the inward-widening slots into the central core.",
    fotiAdvTitle: "Key Advantages:",
    fotiAdvText: "Ideal for external backwashing, scrapers, rotary brush filters, and high external pressure retention.",
    fotiAppTitle: "Typical Installations:",
    fotiAppText: "Intake water screens, automatic self-cleaning filters, resin traps, and well screens.",
    fotiCardEyebrow: "Screen Tube Design",
    fotiCardTitle: "Standard Screen Tube",
    fotiCardText: "Manufactured with precision resistance welding. Available with threaded fittings, weld rings, or custom flange terminations.",
    fotiCardBtn: "View Screen Tubes Specifications",
    fitoBadge: "Inverted Radial / Axial Construction",
    fitoCode: "FITO: Flow In-To-Out",
    fitoHeading: "Smooth Internal Bore with External Support Rods",
    fitoText: "Inverted wedge wire construction places the flat wire face on the inner cylinder wall, backed by external structural ribs. Feed slurry enters inside the tube, retaining oversize solids within the bore while liquid permeates outward.",
    fitoAdvTitle: "Key Advantages:",
    fitoAdvText: "Smooth internal passage allows internal screw augers, dewatering presses, and direct bore cleaning without catching.",
    fitoAppTitle: "Typical Installations:",
    fitoAppText: "Screw press separators, centrifuge baskets, internal distribution headers, and core candles.",
    fitoCardEyebrow: "Full Screen Catalog",
    fitoCardTitle: "Wedge Wire Screen Panels & Cylinders",
    fitoCardText: "Compare flat, curved, and cylindrical wedge wire screen assemblies with custom slot tolerances down to 20 microns.",
    fitoCardBtn: "Explore Wedge Wire Screens",
    sec5Title: "5. Metallurgy & Material Selection Matrix",
    sec5Desc: "Matching alloy grade to operating chemistry, chloride concentrations, temperature, and mechanical stress.",
    labelTypicalUses: "Typical Uses:",
    crossLinksTitle: "Ready to specify your wedge wire geometry?",
    crossLinksDesc: "Review our complete range of cylindrical screen tubes and flat wedge wire screen assemblies, or request custom CAD calculations for open area and burst rating.",
    btnWedgeScreens: "Wedge Wire Screens",
    btnScreenTubes: "Screen Tubes Catalog"
  },
  zh: {
    eyebrow: "技术规格与设计选型",
    heading: "约翰逊楔形滤网工程选型指南",
    lead: "涵盖缝隙开孔尺寸、V型丝型材选型、支撑筋结构评估以及工业分离系统流向计算的全面工程技术参考。",
    sec1Badge: "1. 缝隙开孔与开孔率计算",
    sec1Title: "1. 缝隙开孔与开孔率计算",
    sec1Subtitle: "连续缝隙宽度直接决定颗粒截留精度、水力通量与压降特征。",
    formulaLabel: "开孔率 Open Area (%) = ",
    formulaValue: "[Slot / (Slot + Wire Width)] × 100",
    colSlotMm: "缝隙开孔 (mm / µm)",
    colSlotInch: "英制尺寸 (Inches)",
    colWire: "推荐丝条型材",
    colOpenArea: "典型开孔率",
    colApplications: "典型工业应用",
    sec2Title: "2. V型丝型材（V-Wire）选型",
    sec2Desc: "连续V型截面丝条提供两点接触式拦截，从结构上彻底杜绝颗粒嵌塞与网孔堵塞。",
    principleLabel: "防堵塞原理：",
    principleText: "颗粒仅接触外表面边缘两点。一旦颗粒穿过缝隙边缘，内部开孔逐渐向内扩宽，滤液与合格介质顺畅排出。",
    labelWidth: "宽度",
    labelRelief: "减压角",
    sec3Title: "3. 支撑筋（Support Rod）结构",
    sec3Desc: "电阻焊连接的支撑筋承受整体结构张力、抗压溃强度以及系统周期性冲击载荷。",
    labelAdvantage: "结构优势：",
    labelCommonApp: "典型应用：",
    pitchLabel: "分布与间距：",
    pitchText: "根据系统压差（Delta P）及爆破压力计算要求，标准支撑筋中心距一般在 12 mm 至 50 mm 之间。",
    sec4Title: "4. 流向形式与制造结构几何",
    sec4Desc: "根据过滤与反洗机理，楔形丝滤筒与筛管可定制为标准外进里出或反向里进外出结构。",
    tabFoti: "外进里出 (FOTI)",
    tabFito: "里进外出 (FITO)",
    fotiBadge: "标准环向外表过滤结构",
    fotiCode: "FOTI: 由外向内流向",
    fotiHeading: "外表面光滑平整 · 内部轴向支撑筋",
    fotiText: "在标准结构中，楔形丝平坦面朝外呈环向缠绕在内部轴向支撑筋上。杂质颗粒截留在光滑外表面，洁净滤液经向内扩张的缝隙流入内部中心管通道。",
    fotiAdvTitle: "主要优势：",
    fotiAdvText: "适用于外部反冲洗、刮刀刮刷系统、旋转刷式自清洗过滤器以及高外压截留工况。",
    fotiAppTitle: "典型装配：",
    fotiAppText: "取水滤网、全自动自清洗过滤器、树脂捕集器及水井滤管。",
    fotiCardEyebrow: "筛管规格设计",
    fotiCardTitle: "标准精密楔形丝筛管",
    fotiCardText: "采用精密电阻焊工艺制造，可按需配置螺纹接头、焊接环或定制法兰端头。",
    fotiCardBtn: "查看筛管详细技术规格",
    fitoBadge: "反向内表过滤 / 轴向缝隙结构",
    fitoCode: "FITO: 由内向外流向",
    fitoHeading: "内表面光滑平整 · 外部径向支撑筋",
    fitoText: "反向楔形丝结构将丝条平整面置于内筒壁，外部由结构筋加固。物料浆液自滤管内部进入，截留固体在内部管腔运行，液体向外渗透分离。",
    fitoAdvTitle: "主要优势：",
    fitoAdvText: "光滑内孔通道便于内部螺旋挤压绞龙运行、压榨脱水及内孔直接刮洗，无卡阻磨损。",
    fitoAppTitle: "典型装配：",
    fitoAppText: "螺旋挤压脱水机、离心机筛篮、内部流体分布管及滤芯烛式系统。",
    fitoCardEyebrow: "完整滤网产品目录",
    fitoCardTitle: "楔形滤网平板与滤筒组件",
    fitoCardText: "对比平板、弧形及圆筒形楔形丝滤网组件，缝隙公差精确至 20 微米。",
    fitoCardBtn: "浏览楔形丝滤网全系产品",
    sec5Title: "5. 材质牌号与冶金选型矩阵",
    sec5Desc: "根据操作介质化学特性、氯离子浓度、工作温度与机械应力匹配最优合金牌号。",
    labelTypicalUses: "典型应用领域：",
    crossLinksTitle: "准备选定您的楔形丝结构参数？",
    crossLinksDesc: "查看我们完整的圆筒筛管与平板楔形滤网组件，或联系工程师索取开孔率与耐压爆破强度的定制 CAD 计算。",
    btnWedgeScreens: "楔形丝滤网",
    btnScreenTubes: "筛管产品目录"
  },
  es: {
    eyebrow: "Especificación técnica y diseño",
    heading: "Guía de ingeniería de rejillas de alambre de cuña",
    lead: "Referencia técnica completa para dimensionar aberturas de ranura, perfiles de alambre en V, estructuras de soporte y orientación del flujo en sistemas de separación industrial.",
    sec1Badge: "1. Cálculo de ranura y área abierta",
    sec1Title: "1. Abertura de ranura y cálculo de área abierta",
    sec1Subtitle: "El ancho de ranura continua determina el corte de partículas, el caudal hidráulico y la pérdida de carga.",
    formulaLabel: "Área abierta (%) = ",
    formulaValue: "[Ranura / (Ranura + Ancho de alambre)] × 100",
    colSlotMm: "Abertura de ranura (mm / µm)",
    colSlotInch: "Imperial (Pulgadas)",
    colWire: "Perfil de alambre recomendado",
    colOpenArea: "Área abierta típica",
    colApplications: "Servicio industrial típico",
    sec2Title: "2. Selección de perfil de alambre en V",
    sec2Desc: "Los perfiles de alambre continuo en V ofrecen contacto en dos puntos, evitando la obstrucción y colmatación.",
    principleLabel: "Principio antiobstrucción: ",
    principleText: "Las partículas solo tocan los dos puntos del borde exterior. Al atravesar el borde, la abertura se ensancha hacia el interior permitiendo una descarga limpia.",
    labelWidth: "Ancho",
    labelRelief: "Ángulo de alivio",
    sec3Title: "3. Estructuras de varillas de soporte",
    sec3Desc: "Las varillas soldadas por resistencia resisten la tracción estructural, la presión de colapso y las cargas cíclicas.",
    labelAdvantage: "Ventaja estructural: ",
    labelCommonApp: "Aplicación común: ",
    pitchLabel: "Paso y espaciado: ",
    pitchText: "El espaciado típico entre centros de varilla va de 12 mm a 50 mm según los requisitos de presión diferencial (Delta P) y cálculos de rotura.",
    sec4Title: "4. Dirección de flujo y geometría constructiva",
    sec4Desc: "Los cilindros y tubos de alambre en cuña se configuran en orientación estándar o invertida según el principio de filtración.",
    tabFoti: "De fuera hacia dentro (FOTI)",
    tabFito: "De dentro hacia fuera (FITO)",
    fotiBadge: "Construcción radial estándar",
    fotiCode: "FOTI: Flujo de fuera hacia dentro",
    fotiHeading: "Superficie exterior lisa con varillas de soporte interiores",
    fotiText: "En la configuración estándar, la cara plana del alambre se enrolla circunferencialmente sobre varillas axiales interiores. Los sólidos quedan retenidos en el exterior liso mientras el filtrado limpio fluye hacia el interior.",
    fotiAdvTitle: "Ventajas clave:",
    fotiAdvText: "Ideal para retrolavado externo, rasquetas mecánicas, filtros autolimpiantes rotativos y retención de alta presión externa.",
    fotiAppTitle: "Instalaciones típicas:",
    fotiAppText: "Cribas de toma de agua, filtros autolimpiantes automáticos, trampas de resina y pozos de agua.",
    fotiCardEyebrow: "Diseño de tubos de criba",
    fotiCardTitle: "Tubo de criba estándar",
    fotiCardText: "Fabricado mediante soldadura por resistencia de alta precisión. Disponible con roscas, anillos para soldar o bridas personalizadas.",
    fotiCardBtn: "Ver especificaciones de tubos de criba",
    fitoBadge: "Construcción radial invertida / ranura axial",
    fitoCode: "FITO: Flujo de dentro hacia fuera",
    fitoHeading: "Diámetro interior liso con varillas de soporte exteriores",
    fitoText: "La construcción invertida sitúa la cara plana del alambre en el interior, reforzada con nervaduras estructurales externas. El fluido ingresa al interior y el filtrado drena hacia el exterior.",
    fitoAdvTitle: "Ventajas clave:",
    fitoAdvText: "Paso interno liso para tornillos sinfín de deshidratación, prensas de tornillo y limpieza directa sin atascos.",
    fitoAppTitle: "Instalaciones típicas:",
    fitoAppText: "Separadores de prensa de tornillo, cestas de centrífuga, colectores de distribución interna y velas filtrantes.",
    fitoCardEyebrow: "Catálogo completo de cribas",
    fitoCardTitle: "Paneles y cilindros de alambre en cuña",
    fitoCardText: "Compare conjuntos planos, curvos y cilíndricos con tolerancias de ranura personalizadas de hasta 20 micras.",
    fitoCardBtn: "Explorar rejillas de alambre de cuña",
    sec5Title: "5. Matriz de selección de aleaciones y metalurgia",
    sec5Desc: "Correspondencia entre aleación, composición química, cloruros, temperatura y esfuerzos mecánicos.",
    labelTypicalUses: "Usos típicos:",
    crossLinksTitle: "¿Listo para definir la geometría de su rejilla de alambre en cuña?",
    crossLinksDesc: "Consulte nuestra gama completa de tubos y paneles, o solicite cálculos CAD personalizados para área abierta y resistencia a rotura.",
    btnWedgeScreens: "Rejillas de alambre de cuña",
    btnScreenTubes: "Catálogo de tubos de criba"
  },
  de: {
    eyebrow: "Technische Spezifikation & Auslegung",
    heading: "Spaltsieb-Konstruktionsleitfaden",
    lead: "Umfassender technischer Leitfaden zur Auslegung von Spaltweiten, V-Drahtprofilen, Tragstrukturen und Durchströmungsrichtungen in industriellen Trennanlagen.",
    sec1Badge: "1. Spaltweiten & Berechnung der freien Siebfläche",
    sec1Title: "1. Spaltweite & Berechnung der freien Siebfläche",
    sec1Subtitle: "Kontinuierliche Spaltweiten bestimmen Partikelabscheidung, hydraulische Durchflussleistung und Druckverlust.",
    formulaLabel: "Freie Siebfläche (%) = ",
    formulaValue: "[Spalt / (Spalt + Drahtbreite)] × 100",
    colSlotMm: "Spaltweite (mm / µm)",
    colSlotInch: "Zollmaß (Inches)",
    colWire: "Empfohlenes Drahtprofil",
    colOpenArea: "Typische freie Fläche",
    colApplications: "Typischer Industrieeinsatz",
    sec2Title: "2. Auswahl des V-Drahtprofils",
    sec2Desc: "Kontinuierliche keilförmige Drahtprofile bieten 2-Punkt-Partikelkontakt zur Vermeidung von Verstopfungen.",
    principleLabel: "Selbstreinigungsprinzip: ",
    principleText: "Partikel berühren nur die beiden Außenkanten. Sobald sie den Spalt passieren, erweitert sich der Querschnitt nach innen für verstopfungsfreien Durchfluss.",
    labelWidth: "Breite",
    labelRelief: "Entlastungswinkel",
    sec3Title: "3. Tragstab-Konstruktionen",
    sec3Desc: "Widerstandsgeschweißte Tragstäbe nehmen Zugkräfte, Kollapsdrücke und zyklische Stoßbelastungen auf.",
    labelAdvantage: "Konstruktiver Vorteil: ",
    labelCommonApp: "Typische Anwendung: ",
    pitchLabel: "Teilung & Abstand: ",
    pitchText: "Typische Tragstababstände liegen zwischen 12 mm und 50 mm, abgestimmt auf Differenzdrücke (Delta P) und Berstdrucknachweise.",
    sec4Title: "4. Durchströmungsrichtung & Bauformen",
    sec4Desc: "Spaltsiebe und Siebrohre werden je nach Filtrationsaufgabe in Standard- oder invertierter Durchströmung gefertigt.",
    tabFoti: "Von außen nach innen (FOTI)",
    tabFito: "Von innen nach außen (FITO)",
    fotiBadge: "Standard-Radialkonstruktion",
    fotiCode: "FOTI: Flow Out-To-In",
    fotiHeading: "Glatte Außenfläche mit innenliegenden Tragstäben",
    fotiText: "Beim Standardaufbau liegt die flache Seite des Profildrahts außen und wird auf innenliegende Axialstäbe geschweißt. Partikel verbleiben auf der glatten Außenfläche.",
    fotiAdvTitle: "Hauptvorteile:",
    fotiAdvText: "Ideal für Rückspülung von außen, Schabersysteme, rotierende Bürstenfilter und hohe Außendrücke.",
    fotiAppTitle: "Typische Anlagen:",
    fotiAppText: "Wasserentnahmesiebe, automatische Rückspülfilter, Harzfallen und Brunnenfilterrohre.",
    fotiCardEyebrow: "Siebrohr-Konstruktion",
    fotiCardTitle: "Standard-Spaltsiebrohre",
    fotiCardText: "Präzisionswiderstandsgeschweißt. Lieferbar mit Gewindeenden, Schweißringen oder kundenspezifischen Flanschen.",
    fotiCardBtn: "Technische Daten der Siebrohre ansehen",
    fitoBadge: "Invertierte Radial- / Axialkonstruktion",
    fitoCode: "FITO: Flow In-To-Out",
    fitoHeading: "Glatter Innenkanal mit außenliegenden Tragstäben",
    fitoText: "Bei der invertierten Bauweise liegt die glatte Profilfläche innen, gestützt von Außenrippen. Das Medium wird innen eingeleitet und nach außen abfiltriert.",
    fitoAdvTitle: "Hauptvorteile:",
    fitoAdvText: "Glatter Innenkanal erlaubt Einsatz von Förderschnecken, Entwässerungspressen und Innenreinigung ohne Verschleiß.",
    fitoAppTitle: "Typische Anlagen:",
    fitoAppText: "Schneckenpressen, Zentrifugenkörbe, interne Verteilersysteme und Kerzenfilterelemente.",
    fitoCardEyebrow: "Gesamtes Siebprogramm",
    fitoCardTitle: "Spaltsiebplatten & Siebzylinder",
    fitoCardText: "Vergleichen Sie ebene Platten, Bogensiebe und zylindrische Elemente mit Spaltweiten bis 20 µm.",
    fitoCardBtn: "Spaltsiebe erkunden",
    sec5Title: "5. Werkstoffmatrix & Metallurgie",
    sec5Desc: "Zuordnung geeigneter Legierungen nach Medienzusammensetzung, Chloridgehalt, Temperatur und mechanischer Beanspruchung.",
    labelTypicalUses: "Typische Einsatzbereiche:",
    crossLinksTitle: "Möchten Sie Ihre Spaltsiebgeometrie spezifizieren?",
    crossLinksDesc: "Entdecken Sie unser gesamtes Programm an Siebrohren und Siebplatten oder fordern Sie CAD-Auslegungen für freie Siebflächen und Berstdruck an.",
    btnWedgeScreens: "Spaltsiebe",
    btnScreenTubes: "Siebrohr-Katalog"
  },
  fr: {
    eyebrow: "Spécification technique et conception",
    heading: "Guide d'ingénierie des grilles à fil profilé en V",
    lead: "Référence d'ingénierie détaillée pour le dimensionnement des fentes, le choix des profils de fil en V, les barres de support et le sens d'écoulement.",
    sec1Badge: "1. Calcul de fente et de surface ouverte",
    sec1Title: "1. Ouverture de fente et calcul de la surface ouverte",
    sec1Subtitle: "La largeur de fente continue détermine le seuil de coupure, le débit hydraulique et la perte de charge.",
    formulaLabel: "Surface ouverte (%) = ",
    formulaValue: "[Fente / (Fente + Largeur de fil)] × 100",
    colSlotMm: "Ouverture de fente (mm / µm)",
    colSlotInch: "Pouces (Inches)",
    colWire: "Profil de fil recommandé",
    colOpenArea: "Surface ouverte type",
    colApplications: "Applications industrielles types",
    sec2Title: "2. Sélection du profil de fil en V",
    sec2Desc: "Les fils en coin profilés en V assurent un contact ponctuel en deux points, empêchant colmatage et encrassement.",
    principleLabel: "Principe anti-colmatage : ",
    principleText: "Les particules ne touchent que les deux bords externes. Une fois franchie l'arête de fente, l'espace s'élargit vers l'intérieur pour une évacuation fluide.",
    labelWidth: "Largeur",
    labelRelief: "Dépouille",
    sec3Title: "3. Structures des barres de support",
    sec3Desc: "Les barres soudées par résistance supportent la traction globale, la résistance à l'écrasement et les chocs cycliques.",
    labelAdvantage: "Avantage structurel : ",
    labelCommonApp: "Application courante : ",
    pitchLabel: "Entraxe et espacement : ",
    pitchText: "L'entraxe standard des barres de support varie de 12 mm à 50 mm selon le différentiel de pression (Delta P) et les calculs de résistance à l'éclatement.",
    sec4Title: "4. Sens d'écoulement et géométrie de fabrication",
    sec4Desc: "Les cylindres et tubes filtrants sont configurés en sens conventionnel ou inversé selon le procédé de filtration.",
    tabFoti: "De l'extérieur vers l'intérieur (FOTI)",
    tabFito: "De l'intérieur vers l'extérieur (FITO)",
    fotiBadge: "Construction radiale standard",
    fotiCode: "FOTI : Écoulement extérieur vers intérieur",
    fotiHeading: "Surface externe lisse avec barres de support internes",
    fotiText: "Dans la structure standard, la face plane du fil en coin est enroulée circonférentiellement sur les barres axiales internes. Les débris sont retenus à l'extérieur lisse tandis que le filtrat propre s'écoule vers le centre.",
    fotiAdvTitle: "Principaux avantages :",
    fotiAdvText: "Idéal pour le rétrolavage externe, les racleurs, les filtres autonettoyants à brosses et la rétention sous haute pression extérieure.",
    fotiAppTitle: "Installations types :",
    fotiAppText: "Prises d'eau, filtres automatiques autonettoyants, pièges à résines et forages d'eau.",
    fotiCardEyebrow: "Conception des tubes crépines",
    fotiCardTitle: "Tube crépine standard",
    fotiCardText: "Fabriqué par soudage par résistance de précision. Livrable avec embouts filetés, anneaux à souder ou brides spécifiques.",
    fotiCardBtn: "Voir les spécifications des tubes",
    fitoBadge: "Construction radiale inversée / fente axiale",
    fitoCode: "FITO : Écoulement intérieur vers extérieur",
    fitoHeading: "Alésage intérieur lisse avec barres de support externes",
    fitoText: "La structure inversée place la face plane du fil à l'intérieur, renforcée par des nervures extérieures. La pulpe entre par l'intérieur et le filtrat traverse vers l'extérieur.",
    fitoAdvTitle: "Principaux avantages :",
    fitoAdvText: "Alésage interne lisse permettant le passage de vis d'extrusion, de presses d'égouttage et le raclage intérieur sans accrochage.",
    fitoAppTitle: "Installations types :",
    fitoAppText: "Séparateurs à vis de presse, paniers de centrifugeuse, rampes de distribution et bougies filtrantes.",
    fitoCardEyebrow: "Catalogue complet de grilles",
    fitoCardTitle: "Panneaux et cylindres à fils en coin",
    fitoCardText: "Comparez les ensembles plats, courbes et cylindriques avec des tolérances de fente sur mesure jusqu'à 20 microns.",
    fitoCardBtn: "Explorer les grilles à fil profilé",
    sec5Title: "5. Matrice de sélection métallurgique et nuances",
    sec5Desc: "Adéquation des nuances d'acier avec la chimie du fluide, la teneur en chlorures, la température et les contraintes mécaniques.",
    labelTypicalUses: "Usages types :",
    crossLinksTitle: "Prêt à spécifier la géométrie de vos grilles à fil profilé ?",
    crossLinksDesc: "Découvrez notre gamme complète de tubes et panneaux ou demandez des calculs CAD sur mesure de surface ouverte et de tenue à la pression.",
    btnWedgeScreens: "Grilles à fils profilés",
    btnScreenTubes: "Catalogue des tubes crépines"
  },
  pt: {
    eyebrow: "Especificação técnica e dimensionamento",
    heading: "Guia de engenharia de telas de arame em cunha",
    lead: "Referência completa para dimensionamento de aberturas de ranhura, perfis de arame V, barras de suporte e sentido de fluxo em sistemas industriais.",
    sec1Badge: "1. Cálculo de ranhura e área aberta",
    sec1Title: "1. Abertura de ranhura e cálculo de área aberta",
    sec1Subtitle: "A largura contínua da ranhura determina o ponto de corte, a capacidade hidráulica e a perda de carga.",
    formulaLabel: "Área aberta (%) = ",
    formulaValue: "[Ranhura / (Ranhura + Largura do arame)] × 100",
    colSlotMm: "Abertura de ranhura (mm / µm)",
    colSlotInch: "Polegadas (Inches)",
    colWire: "Perfil de arame recomendado",
    colOpenArea: "Área aberta típica",
    colApplications: "Serviço industrial típico",
    sec2Title: "2. Seleção de perfil de arame em V",
    sec2Desc: "Perfis contínuos em formato V proporcionam contato em dois pontos, evitando entupimento e colmatação.",
    principleLabel: "Princípio antientupimento: ",
    principleText: "As partículas tocam apenas as duas bordas externas. Ao cruzar a fenda, o canal se alarga internamente permitindo descarga fluida.",
    labelWidth: "Largura",
    labelRelief: "Ângulo de alívio",
    sec3Title: "3. Estruturas de barras de suporte",
    sec3Desc: "As barras soldadas por resistência suportam a tensão estrutural, pressão de colapso e impactos cíclicos.",
    labelAdvantage: "Vantagem estrutural: ",
    labelCommonApp: "Aplicação comum: ",
    pitchLabel: "Passo e espaçamento: ",
    pitchText: "O espaçamento típico entre eixos de barras varia de 12 mm a 50 mm conforme o diferencial de pressão (Delta P) e cálculos de ruptura.",
    sec4Title: "4. Sentido de fluxo e geometria construtiva",
    sec4Desc: "Cilindros e tubos filtrantes de arame em cunha são produzidos em orientação padrão ou invertida segundo o sistema de filtragem.",
    tabFoti: "De fora para dentro (FOTI)",
    tabFito: "De dentro para fora (FITO)",
    fotiBadge: "Construção radial padrão",
    fotiCode: "FOTI: Fluxo de fora para dentro",
    fotiHeading: "Superfície externa lisa com barras axiais internas",
    fotiText: "Na configuração padrão, a face plana do arame perfilado envolve circunferencialmente as barras axiais internas. Sólidos ficam retidos no exterior liso enquanto o filtrado limpo flui para o interior.",
    fotiAdvTitle: "Principais vantagens:",
    fotiAdvText: "Ideal para retrolavagem externa, raspadores mecânicos, filtros autolimpantes de escova rotativa e alta pressão externa.",
    fotiAppTitle: "Instalações típicas:",
    fotiAppText: "Captações de água, filtros autolimpantes automáticos, retentores de resina e poços artesianos.",
    fotiCardEyebrow: "Projeto de tubos de tela",
    fotiCardTitle: "Tubo de tela convencional",
    fotiCardText: "Fabricado por soldagem de resistência de precisão. Disponível com terminais roscados, anéis de solda ou flanges especiais.",
    fotiCardBtn: "Ver especificações de tubos de tela",
    fitoBadge: "Construção radial invertida / ranhura axial",
    fitoCode: "FITO: Fluxo de dentro para fora",
    fitoHeading: "Canal interno liso com barras estruturais externas",
    fitoText: "A construção invertida coloca a face plana do arame no diâmetro interno, apoiada por nervuras externas. O fluido entra pelo interior e o líquido permeia para fora.",
    fitoAdvTitle: "Principais vantagens:",
    fitoAdvText: "Passagem interna lisa permite operação de roscas transportadoras, prensas de desaguamento e raspagem sem atrito.",
    fitoAppTitle: "Instalações típicas:",
    fitoAppText: "Separadores de prensa helicoidal, cestos centrífugos, distribuidores internos e elementos de vela.",
    fitoCardEyebrow: "Catálogo completo de telas",
    fitoCardTitle: "Painéis e cilindros de arame em cunha",
    fitoCardText: "Compare conjuntos planos, curvos e cilíndricos com tolerâncias de ranhura personalizadas até 20 mícrons.",
    fitoCardBtn: "Explorar telas de arame em cunha",
    sec5Title: "5. Matriz de seleção metalúrgica e ligas",
    sec5Desc: "Compatibilização de ligas com química operacional, teor de cloretos, temperatura e esforços mecânicos.",
    labelTypicalUses: "Aplicações típicas:",
    crossLinksTitle: "Pronto para especificar a geometria da sua tela de arame em cunha?",
    crossLinksDesc: "Conheça nossa linha completa de tubos e painéis planos ou solicite cálculos CAD personalizados de área aberta e pressão de ruptura.",
    btnWedgeScreens: "Telas de arame em cunha",
    btnScreenTubes: "Catálogo de tubos de tela"
  }
};
export default function WedgeGuide() {
  const {
    lang
  } = useLanguage();
  const [activeFlowMode, setActiveFlowMode] = useState<"foti" | "foto">("foti");
  const copy = pick(lang, copyData);
  return <section id="wedgewireengineeringguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono mf-caption bg-muted text-foreground">
            {copy.eyebrow}
          </Badge>
          <h2 className="mf-h2 text-foreground">
            {copy.heading}
          </h2>
          <p className="mt-4 mf-lead text-muted-foreground">
            {copy.lead}
          </p>
        </div>

        {/* Section 1: Slot Openings & Open Area Calculations */}
        <Reveal className="mb-16 md:mb-20">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                  <Gauge className="size-5" aria-hidden="true" />
                </span>
                <h3 className="mf-h3">
                  {copy.sec1Title}
                </h3>
              </div>
              <p className="text-[13px] text-muted-foreground">
                {copy.sec1Subtitle}
              </p>
            </div>
            
            <div className="bg-muted p-3 rounded-lg border border-border flex items-center gap-3">
              <div className="text-[13px] font-mono">
                <span className="text-muted-foreground">{copy.formulaLabel}</span>
                <span className="text-foreground font-semibold">{copy.formulaValue}</span>
              </div>
            </div>
          </div>

          <div className="border border-border rounded-lg bg-card overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead className="font-semibold text-foreground">
                    {copy.colSlotMm}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    {copy.colSlotInch}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    {copy.colWire}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    {copy.colOpenArea}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    {copy.colApplications}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slotOpenings.map((item, i) => <TableRow key={item.slotMm} data-index={i} className="hover:bg-muted/30">
                    <TableCell className="font-mono font-medium text-foreground">{item.slotMm}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">{item.slotInch}</TableCell>
                    <TableCell className="font-medium text-primary">{item.standardWire}</TableCell>
                    <TableCell className="font-mono font-medium">{item.openArea}</TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">
                      {pick(lang, item.applications)}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </Reveal>

        {/* Section 2: Profile Wires & Support Rods Grid */}
        <Reveal className="mb-16 md:mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Profile Wire Geometry */}
            <Card className="border-border rounded-lg">
              <CardHeader className="border-b border-border pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                    <SlidersHorizontal className="size-5" aria-hidden="true" />
                  </span>
                  <CardTitle className="mf-h3 font-semibold">
                    {copy.sec2Title}
                  </CardTitle>
                </div>
                <CardDescription>
                  {copy.sec2Desc}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="bg-muted/40 p-4 rounded-lg border border-border flex items-start gap-3">
                  <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="text-sm text-muted-foreground">
                    <strong className="text-foreground font-medium">
                      {copy.principleLabel}
                    </strong>
                    {copy.principleText}
                  </div>
                </div>

                <div className="space-y-3">
                  {wireProfiles.map((wire, i) => <div key={wire.code} data-index={i} className="p-3 rounded-lg border border-border/80 bg-background hover:border-primary/40 transition-[border-color]">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono font-bold text-sm text-foreground">{wire.code}</span>
                        <Badge variant="outline" className="text-[13px] font-mono bg-muted text-foreground">
                          {pick(lang, wire.strength)}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[13px] text-muted-foreground font-mono mb-2">
                        <div>{copy.labelWidth}: {wire.width}</div>
                        <div>{copy.labelRelief}: {wire.reliefAngle}</div>
                      </div>
                      <p className="text-[13px] text-muted-foreground leading-normal">
                        {pick(lang, wire.description)}
                      </p>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {/* Support Rods & Structure */}
            <Card className="border-border rounded-lg">
              <CardHeader className="border-b border-border pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                    <Layers className="size-5" aria-hidden="true" />
                  </span>
                  <CardTitle className="mf-h3 font-semibold">
                    {copy.sec3Title}
                  </CardTitle>
                </div>
                <CardDescription>
                  {copy.sec3Desc}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-4">
                  {supportRods.map((rod, i) => <div key={rod.type.en} data-index={i} className="p-4 rounded-lg border border-border bg-background space-y-2">
                      <div className="font-semibold text-sm text-foreground">
                        {pick(lang, rod.type)}
                      </div>
                      <div className="text-[13px] font-mono text-primary bg-muted px-2 py-1 rounded inline-block border border-border">
                        {pick(lang, rod.geometry)}
                      </div>
                      <p className="text-[13px] text-muted-foreground">
                        <span className="font-medium text-foreground">{copy.labelAdvantage}</span>
                        {pick(lang, rod.primaryBenefit)}
                      </p>
                      <p className="text-[13px] text-muted-foreground">
                        <span className="font-medium text-foreground">{copy.labelCommonApp}</span>
                        {pick(lang, rod.bestFor)}
                      </p>
                    </div>)}
                </div>

                <div className="p-3.5 rounded-lg bg-muted border border-border text-[13px] text-foreground">
                  <span className="font-semibold">{copy.pitchLabel}</span>
                  {copy.pitchText}
                </div>
              </CardContent>
            </Card>

          </div>
        </Reveal>

        {/* Section 3: Flow Direction (Inside-Out vs Outside-In) */}
        <Reveal className="mb-16 md:mb-20">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                <ArrowDownUp className="size-5" aria-hidden="true" />
              </span>
              <h3 className="mf-h3">
                {copy.sec4Title}
              </h3>
            </div>
            <p className="text-[13px] text-muted-foreground">
              {copy.sec4Desc}
            </p>
          </div>

          <Tabs defaultValue="foti" className="w-full" onValueChange={v => setActiveFlowMode(v as "foti" | "foto")}>
            <TabsList className="grid grid-cols-2 max-w-md mb-6">
              <TabsTrigger value="foti" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {copy.tabFoti}
              </TabsTrigger>
              <TabsTrigger value="foto" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {copy.tabFito}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="foti" className="mt-0">
              <Card className="border-border rounded-lg">
                <CardContent className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-primary text-primary-foreground border-transparent">
                        {copy.fotiBadge}
                      </Badge>
                      <span className="text-[13px] font-mono text-muted-foreground">
                        {copy.fotiCode}
                      </span>
                    </div>
                    <h4 className="text-lg font-bold text-foreground">
                      {copy.fotiHeading}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {copy.fotiText}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      <div className="p-3 rounded-md bg-muted/40 border border-border text-[13px]">
                        <span className="font-semibold block text-foreground mb-1">
                          {copy.fotiAdvTitle}
                        </span>
                        {copy.fotiAdvText}
                      </div>
                      <div className="p-3 rounded-md bg-muted/40 border border-border text-[13px]">
                        <span className="font-semibold block text-foreground mb-1">
                          {copy.fotiAppTitle}
                        </span>
                        {copy.fotiAppText}
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/30 p-5 rounded-lg border border-border flex flex-col justify-between">
                    <div>
                      <div className="text-[13px] font-mono uppercase tracking-wider text-muted-foreground mb-2">
                        {copy.fotiCardEyebrow}
                      </div>
                      <div className="text-sm font-semibold text-foreground mb-1">
                        {copy.fotiCardTitle}
                      </div>
                      <p className="text-[13px] text-muted-foreground mb-4">
                        {copy.fotiCardText}
                      </p>
                    </div>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <Link to="/ScreenTubesPage">
                        <span>{copy.fotiCardBtn}</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="foto" className="mt-0">
              <Card className="border-border rounded-lg">
                <CardContent className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-muted text-foreground">
                        {copy.fitoBadge}
                      </Badge>
                      <span className="text-[13px] font-mono text-muted-foreground">
                        {copy.fitoCode}
                      </span>
                    </div>
                    <h4 className="text-lg font-bold text-foreground">
                      {copy.fitoHeading}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {copy.fitoText}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      <div className="p-3 rounded-md bg-muted/40 border border-border text-[13px]">
                        <span className="font-semibold block text-foreground mb-1">
                          {copy.fitoAdvTitle}
                        </span>
                        {copy.fitoAdvText}
                      </div>
                      <div className="p-3 rounded-md bg-muted/40 border border-border text-[13px]">
                        <span className="font-semibold block text-foreground mb-1">
                          {copy.fitoAppTitle}
                        </span>
                        {copy.fitoAppText}
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/30 p-5 rounded-lg border border-border flex flex-col justify-between">
                    <div>
                      <div className="text-[13px] font-mono uppercase tracking-wider text-muted-foreground mb-2">
                        {copy.fitoCardEyebrow}
                      </div>
                      <div className="text-sm font-semibold text-foreground mb-1">
                        {copy.fitoCardTitle}
                      </div>
                      <p className="text-[13px] text-muted-foreground mb-4">
                        {copy.fitoCardText}
                      </p>
                    </div>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <Link to="/WedgeWireScreenPage">
                        <span>{copy.fitoCardBtn}</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </Reveal>

        {/* Section 4: Material Selection Matrix */}
        <Reveal className="mb-16 md:mb-20">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
              <ShieldCheck className="size-5" aria-hidden="true" />
            </span>
            <h3 className="mf-h3">
              {copy.sec5Title}
            </h3>
          </div>
          <p className="text-[13px] text-muted-foreground mb-6">
            {copy.sec5Desc}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {alloys.map((alloy, i) => <div key={alloy.grade} data-index={i} className="p-5 rounded-lg border border-border bg-card flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[13px] font-mono text-muted-foreground">
                      {pick(lang, alloy.category)}
                    </span>
                    <Badge variant="outline" className="text-[13px] font-mono bg-muted text-foreground">
                      {pick(lang, alloy.costIndex)}
                    </Badge>
                  </div>
                  <h4 className="font-bold text-base text-foreground mb-2">{alloy.grade}</h4>
                  <p className="text-[13px] text-muted-foreground mb-3 leading-relaxed">
                    {pick(lang, alloy.resistance)}
                  </p>
                </div>
                <div className="pt-3 border-t border-border/60 text-[13px] text-muted-foreground">
                  <span className="font-semibold text-foreground block mb-0.5">
                    {copy.labelTypicalUses}
                  </span>
                  {pick(lang, alloy.typicalUse)}
                </div>
              </div>)}
          </div>
        </Reveal>

        {/* Engineering Cross-Links Banner */}
        <Reveal className="p-8 md:p-10 rounded-lg bg-muted/60 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl space-y-2 text-center md:text-left">
            <h3 className="mf-h3 text-foreground">
              {copy.crossLinksTitle}
            </h3>
            <p className="text-sm text-muted-foreground">
              {copy.crossLinksDesc}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="outline" size="lg">
              <Link to="/WedgeWireScreenPage" className="inline-flex items-center gap-2">
                <span>{copy.btnWedgeScreens}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/ScreenTubesPage" className="inline-flex items-center gap-2">
                <span>{copy.btnScreenTubes}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </Reveal>

      </div>
    </section>;
}